import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import type { Game } from '@goodbuild/shared';
import { Button, Card, Eyebrow, Heading, Icon, PageHead, ProgressBar, Text } from '../ui/index.js';
import { useGames } from '../api/hooks.js';
import { capabilities } from '../platform/index.js';
import { native, type QueuedJob } from '../platform/tauri.js';
import {
  clearFinished,
  refreshDownloads,
  useDownloads,
  type FinishedDownload,
} from '../downloads/store.js';
import { downloadNumbers, progressSummary } from '../downloads/progress.js';
import { Focusable, FocusZone } from '../components/focus/Focusable.js';
import { EmptyState } from '../components/layout/Loading.js';
import { errorMessage } from '../lib/errors.js';
import { formatBytes, formatDuration, formatSpeed } from '../lib/format.js';

export const Route = createFileRoute('/downloads')({ component: Downloads });

/** What each row calls itself. The phase is the detail; this is the headline. */
const STATE_LABELS: Record<string, string> = {
  queued: 'Waiting',
  downloading: 'Downloading',
  installing: 'Installing',
  paused: 'Paused',
  verifying: 'Verifying',
  uninstalling: 'Removing',
  installed: 'Installed',
  error: 'Stopped',
  locked: 'Needs a membership',
};

/**
 * What the job is doing inside the state, for the phases where the state alone is not enough.
 *
 * A download that says "Waiting" for twenty seconds while it reads a 30 GB manifest looks
 * stuck. Saying which of the two it is costs a word and answers the question.
 */
const PHASE_NOTES: Record<string, string> = {
  manifest: 'reading the build',
  planning: 'working out what changed',
  runtime: 'setting up Proton',
  committing: 'putting the files in place',
  verifying: 'checking the files',
};

const FINISHED_LABELS: Record<FinishedDownload['state'], string> = {
  installed: 'Installed',
  not_installed: 'Cancelled',
  error: 'Stopped',
  locked: 'Needs a membership',
};

/**
 * The download queue, on its own screen.
 *
 * It exists because a download is not part of the game it belongs to: four of them can be in
 * the air at once, only one is moving, and the order they are in is a thing somebody may want
 * to change. None of that fits under a button on a game page.
 *
 * One job runs and the rest wait, which is what the native queue enforces
 * (`04-launcher-and-patcher.md` section 2.3). This screen is where that order is visible and
 * where it can be changed.
 */
function Downloads() {
  const navigate = useNavigate();
  const { queue, finished, loaded } = useDownloads();
  const { data: games } = useGames();

  const byId = React.useMemo(() => {
    const map = new Map<string, Game>();
    for (const game of games ?? []) map.set(game.id, game);
    return map;
  }, [games]);

  if (!capabilities.installWindowsGames && !capabilities.installAndroidGames) {
    return (
      <FocusZone focusKey="downloads" direction="column" className="gb-stack gb-stack--loose">
        <PageHead
          eyebrow="On this machine"
          title="Downloads."
          description="Games download in the app. The browser can browse the catalog, but the builds land on a machine that can run them."
        />
        <EmptyState
          title="Nothing downloads here."
          body="Get the app for Windows, the Steam Deck or Android, and this is where its queue will be."
        />
        <div className="gb-row">
          <Focusable
            autoFocus
            onSelect={() => void navigate({ to: '/download' })}
            selectLabel="Get the app"
            className="gb-focusable--flat"
          >
            <Button size="3">
              <Icon name="download" size={16} />
              Get the app
            </Button>
          </Focusable>
        </div>
      </FocusZone>
    );
  }

  // Compressed bytes, which is what goes over the wire and therefore what the estimate is made
  // of. The size shown next to a game on its own page is the installed size, and they differ.
  const remaining = queue.reduce(
    (total, job) => total + Math.max(0, job.bytesTotal - job.bytesDone),
    0,
  );
  const speed = queue.reduce((total, job) => total + job.speedBps, 0);

  return (
    <FocusZone focusKey="downloads" direction="column" className="gb-stack gb-stack--loose">
      <PageHead
        eyebrow="On this machine"
        title="Downloads."
        description={
          queue.length === 0
            ? 'Anything installing or updating shows up here, with what it is doing and how long it has left.'
            : `One at a time, in this order. ${formatBytes(remaining)} left${
                speed > 0 ? ` at ${formatSpeed(speed)}/s` : ''
              }.`
        }
      />

      {queue.length === 0 ? (
        <EmptyState
          title={loaded ? 'Nothing downloading.' : 'Reading the queue.'}
          body={
            loaded
              ? 'Press Install on a game and it lands here. Queue as many as you like; they download one after another.'
              : undefined
          }
        />
      ) : (
        <FocusZone focusKey="downloads-queue" direction="column" className="gb-stack">
          {queue.map((job, index) => (
            <DownloadRow
              key={job.jobId}
              job={job}
              game={byId.get(job.gameId) ?? null}
              last={index === queue.length - 1}
              autoFocus={index === 0}
            />
          ))}
        </FocusZone>
      )}

      {finished.length > 0 ? (
        <section className="gb-stack gb-stack--tight">
          <div className="gb-row gb-row--between">
            <Heading size="4">{'Finished'}</Heading>
            <Focusable onSelect={clearFinished} selectLabel="Clear" className="gb-focusable--flat">
              <Button size="1" variant="ghost">
                {'Clear'}
              </Button>
            </Focusable>
          </div>
          {finished.map((entry) => (
            <FinishedRow
              key={`${entry.gameId}-${entry.at}`}
              entry={entry}
              game={byId.get(entry.gameId) ?? null}
            />
          ))}
        </section>
      ) : null}
    </FocusZone>
  );
}

/** The small capsule if the game has one, otherwise whatever else it carries. */
function thumbFor(game: Game | null): string | null {
  if (!game) return null;
  return game.smallImageUrl ?? game.coverImageUrl ?? game.capsuleImageUrl ?? null;
}

function DownloadRow({
  job,
  game,
  last,
  autoFocus,
}: {
  job: QueuedJob;
  game: Game | null;
  last: boolean;
  autoFocus: boolean;
}) {
  const navigate = useNavigate();
  // A row has no event stream of its own — the queue is the whole of what it knows — but the
  // reading of those figures is the same one the game page makes, so it is made in one place.
  const numbers = downloadNumbers(null, job);
  const figures = progressSummary(numbers, {
    bytes: formatBytes,
    speed: formatSpeed,
    duration: formatDuration,
  });
  const title = game?.title ?? job.slug;
  const thumb = thumbFor(game);

  const move = (delta: number) => {
    void native.downloadsReorder(job.jobId, delta).then(refreshDownloads);
  };

  const label = STATE_LABELS[job.state] ?? job.state;
  const note = job.running ? PHASE_NOTES[job.phase] : undefined;

  return (
    <Card size="2" className="gb-download">
      <div className="gb-download__head">
        {thumb ? (
          <img className="gb-download__art" src={thumb} alt="" loading="lazy" />
        ) : (
          <span className="gb-download__art gb-download__art--empty" aria-hidden="true" />
        )}

        <div className="gb-download__title">
          {/* The name opens the game. Without a game in the catalog there is nowhere to open,
              so the row is read-only rather than a button that does nothing. */}
          <Focusable
            autoFocus={autoFocus}
            onSelect={
              game
                ? () => void navigate({ to: '/games/$slug', params: { slug: game.slug } })
                : undefined
            }
            selectLabel={title}
            className="gb-focusable--flat"
          >
            <Heading size="4" render={<h3 />}>
              {title}
            </Heading>
          </Focusable>
          <div className="gb-row gb-row--tight">
            <Eyebrow tone={job.running ? 'lemon' : 'quiet'} small>
              {job.paused ? label : `${label}${note ? ` · ${note}` : ''}`}
            </Eyebrow>
            {/* The place in the line, for everything that is not the one being worked on.
                Without it, three rows all reading "Waiting" say nothing about which is next. */}
            {!job.running && !job.paused ? (
              <Text size="1" color="gray" mono>
                {`#${job.position + 1} in the queue`}
              </Text>
            ) : null}
          </div>
        </div>

        <Text size="1" color="gray" mono className="gb-download__numbers">
          {figures}
        </Text>
      </div>

      {/* A bar with nothing measured yet gets no percentage, because inventing one is how a bar
          earns a reputation — but nor does it draw a nought, which reads as a job that has
          stalled. It sweeps until there is a figure to stand behind. */}
      <ProgressBar value={numbers.fraction} label={`${title}, ${label}`} />

      {job.error ? (
        <Text size="1" color="gray">
          {errorMessage(job.error)}
        </Text>
      ) : null}

      <div className="gb-row gb-row--between">
        <div className="gb-row gb-row--tight">
          {job.paused ? (
            <Focusable onSelect={() => void native.gameResume(job.gameId)} selectLabel="Resume">
              <Button size="2">
                <Icon name="play" size={12} />
                {'Resume'}
              </Button>
            </Focusable>
          ) : (
            <Focusable onSelect={() => void native.gamePause(job.gameId)} selectLabel="Pause">
              <Button size="2" variant="surface">
                {'Pause'}
              </Button>
            </Focusable>
          )}
          <Focusable onSelect={() => void native.gameCancel(job.gameId)} selectLabel="Cancel">
            <Button size="2" variant="ghost" color="danger">
              {'Cancel'}
            </Button>
          </Focusable>
        </div>

        {/* Reordering is the point of a queue being visible: deciding what downloads tonight
            without having to pause the other three. */}
        <div className="gb-row gb-row--tight">
          <Focusable
            onSelect={() => move(-1)}
            selectLabel="Move up"
            disabled={job.position === 0}
            className="gb-focusable--flat"
          >
            <Button size="1" variant="ghost" aria-label={`Move ${title} up the queue`}>
              <Icon name="chevron-left" size={14} className="gb-download__turn" />
            </Button>
          </Focusable>
          <Focusable
            onSelect={() => move(1)}
            selectLabel="Move down"
            disabled={last}
            className="gb-focusable--flat"
          >
            <Button size="1" variant="ghost" aria-label={`Move ${title} down the queue`}>
              <Icon name="chevron-right" size={14} className="gb-download__turn" />
            </Button>
          </Focusable>
        </div>
      </div>
    </Card>
  );
}

function FinishedRow({ entry, game }: { entry: FinishedDownload; game: Game | null }) {
  const navigate = useNavigate();
  const title = game?.title ?? entry.slug;

  return (
    <Focusable
      onSelect={
        game ? () => void navigate({ to: '/games/$slug', params: { slug: game.slug } }) : undefined
      }
      selectLabel={title}
    >
      <Card size="1" className="gb-row gb-row--between">
        <div className="gb-row gb-row--tight">
          <Icon name={entry.state === 'installed' ? 'check' : 'close'} size={14} />
          <Text size="2">{title}</Text>
        </div>
        <Text size="1" color="gray">
          {entry.error ? errorMessage(entry.error) : FINISHED_LABELS[entry.state]}
        </Text>
      </Card>
    </Focusable>
  );
}
